/* eslint-disable @typescript-eslint/restrict-template-expressions */
import styled from 'styled-components';
import palette from '@styles/theme/palette';
import { PANEL_BODY_MAX_HEIGHT } from '@components/TranscriptPanel/styles';

// Tab bar height (label row at the top of the Card). Used to derive the Card's min-height so
// the embedded transcript body has the same vertical budget as its standalone Panel sibling.
const TAB_BAR_HEIGHT = 52;
const CARD_MIN_HEIGHT = PANEL_BODY_MAX_HEIGHT + TAB_BAR_HEIGHT;

// Adaptive tokens flow from `@styles/global` as CSS variables driven by the creator's chosen
// `campaign.style.background` and `responseButtonBackground`. Surface, text, border, hover,
// and the active-line wash flip for light vs dark backgrounds; the accent (active tab text,
// rail, active "AI" badge) follows the creator's CTA color so the panel harmonizes with the
// rest of the share page. Snozzberry remains the fallback when no overrides are set.
const COLORS = {
  accent: `var(--global-accent, ${palette.primary.main})`,
  surface: `var(--global-background, ${palette.bianca})`,
  border: `var(--global-border, ${palette.pampas})`,
  fg: `var(--global-foreground, ${palette.common.black})`,
  fgMuted: `var(--global-foreground-muted, ${palette.naturalGray})`,
};
const RAIL_EASE = '220ms cubic-bezier(0.4, 0, 0.2, 1)';

export const Card = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: ${CARD_MIN_HEIGHT}px;
  background: ${COLORS.surface};
  border: 1px solid ${COLORS.border};
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 4px 1px rgba(193, 199, 205, 0.48);
`;

export const TabBar = styled.div`
  position: relative;
  display: flex;
  align-items: stretch;
  padding: 6px 6px 0;
  background: ${COLORS.surface};
  border-bottom: 1px solid ${COLORS.border};
  flex-shrink: 0;
`;

export const TabButton = styled.button<{ active: boolean }>`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 16px;
  background: transparent;
  border: none;
  font-family: Lexend, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: ${({ active }) => (active ? COLORS.accent : COLORS.fgMuted)};
  cursor: pointer;
  transition: color 150ms cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    color: ${({ active }) => (active ? COLORS.accent : COLORS.fg)};
  }
`;


export const TabRail = styled.div<{ index: number; tabCount: number }>`
  position: absolute;
  left: 0;
  bottom: -1px;
  width: ${({ tabCount }) => 100 / Math.max(tabCount, 1)}%;
  height: 2px;
  background: ${COLORS.accent};
  transform: translateX(${({ index }) => index * 100}%);
  transition: transform ${RAIL_EASE};
`;

export const Body = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

export const TabBody = styled.div<{ active: boolean }>`
  display: ${({ active }) => (active ? 'flex' : 'none')};
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;
